import React, { useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Alert, Button, IconButton, Menu, MenuItem } from "@mui/material";
import Select from "@components/Select/Select";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { SIDE_DRAWER_MAX_WIDTH } from "@constants/layout.style";
import { useBayHireBayOptions } from "@features/bay-hire/hooks/useBayHireQueries";
import type { BayHireBayOption } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

/**
 * DFT-0044 slice C / `03_Bay Hire License.png`: the Shipping Agent's own bay selection.
 *
 * <p>Replaces the free `Number of Bays` input. That field was added by DFT-0013 "so Operations has a
 * reference point during pre-approval bay selection" - Operations no longer selects bays, so there is
 * nothing for it to be a reference point for, and `numberOfBays` is now DERIVED server-side from this
 * list. A typed count would be silently discarded (verified against the running stack: sending 99 with
 * two bays stored 2).
 */
export interface SelectedBay {
  referenceDataBayId: string;
  bayNoSnapshot?: string;
  sizeLabel?: string;
}

interface BaySelectionSectionProps {
  /**
   * DFT-0110: the chosen `Port` (POF / POD). Scopes the Reference Data catalogue query itself
   * (`useBayHireBayOptions`'s own `portCode` param) - unlike `bayPreference` below, which filters
   * the ALREADY-fetched options client-side, this determines what is fetched in the first place.
   */
  port: string;
  /** The chosen `Request Bay` (INSIDE_PORT / OUTSIDE_PORT). Drives which bays may be offered. */
  bayPreference: string;
  value: SelectedBay[];
  onChange: (bays: SelectedBay[]) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * `Size (m²)` from the option's criteria.
 *
 * The criterion code is `size`, lower-case - `P_SIZE` is the database column, not what Reference Data
 * emits - and the values carry one of eight-plus unit spellings (`54Sq.Mtr`, `54Sq.Mtr.`, `45Sq. Mt.`,
 * `54SQMTR`, `54 SQM`, bare `54`). The header already says (m²), so the leading number is shown.
 *
 * A `*<n>` multiplier is shown VERBATIM: 81 of the 215 catalogue rows carry one, and a bay recorded as
 * `80*2` is not 80 m² - printing 80 would be a wrong number presented as a right one.
 */
export const bayOptionSizeLabel = (option: BayHireBayOption): string => {
  const raw = (option.criteria ?? []).find(
    (criterion) => (criterion.code ?? "").toLowerCase() === "size",
  )?.value;
  if (!raw || String(raw).trim() === "") return "-";
  const value = String(raw).trim();
  if (value.includes("*")) return value;
  const leading = /^(\d+(?:\.\d+)?)/.exec(value);
  return leading ? leading[1] : value;
};

/**
 * The "Add bay" drawer's own dropdown label: Bay No AND Description together (e.g. "CB023 - BAY
 * No.023"), not just the Bay No. Choosing blind between otherwise-identical-looking catalogue ids
 * was the actual complaint this addresses - the Description is exactly what already distinguishes
 * bays once added (the Bays table's own second column), so showing it here too lets the same
 * distinction inform the choice, not just confirm it afterwards.
 */
export const bayOptionSelectLabel = (option: BayHireBayOption): string => {
  const bayNo = option.id ?? "";
  const description = option.bayNo ?? "";
  if (bayNo && description) return `${bayNo} - ${description}`;
  return bayNo || description || "";
};

const BaySelectionSection: React.FC<BaySelectionSectionProps> = ({
  port,
  bayPreference,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The bay chosen in the drawer but NOT yet added - the wireframe commits on the footer button, so
  // the selection has to live somewhere until then.
  const [pendingBayId, setPendingBayId] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; bayId: string } | null>(null);
  // DFT-0110: undefined (not an empty string) when no port is chosen yet, so the query is not
  // scoped to a portCode of "" - useBayHireBayOptions() with no args is the "return everything"
  // shape, matching its own pre-DFT-0110 default behavior for a not-yet-chosen port.
  const { data: bayOptions = [], isError } = useBayHireBayOptions(port || undefined);

  const selectedIds = useMemo(() => new Set(value.map((bay) => bay.referenceDataBayId)), [value]);

  /**
   * Offered bays are filtered to the chosen Request Bay, and already-selected ones are excluded.
   *
   * Both filters exist so the UI cannot build a selection the server will reject: the domain requires
   * every bay to share one inside/outside value AND to match `bayPreference` (slice C), and refuses
   * duplicates because `numberOfBays` is derived from this list's length. Offering an invalid choice
   * and then surfacing a 400 would be a worse experience than not offering it.
   */
  const selectableOptions = useMemo(
    () =>
      bayOptions.filter(
        (option) =>
          !!option.id &&
          !selectedIds.has(option.id) &&
          (option.areaCode ?? "").toUpperCase() === bayPreference.toUpperCase(),
      ),
    [bayOptions, selectedIds, bayPreference],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Cancel must discard the pending choice, otherwise reopening the drawer would silently
    // pre-arm the bay the user just declined to add.
    setPendingBayId("");
  };

  const confirmAddBay = () => {
    const option = selectableOptions.find((candidate) => candidate.id === pendingBayId);
    if (!option?.id) return;
    onChange([
      ...value,
      {
        // Bay No, Description and Size are ALL read from the Reference Data catalogue option here -
        // none of them are typed by the user, matching the BFF's own re-derivation on submit
        // (deriveBaySnapshotsFromReferenceData discards anything client-supplied). This form's copy
        // is only a preview of what the server will store; it must never diverge from it.
        //
        // Column mapping, per explicit correction: DB {id, bayNo, size} -> UI {Bay No, Description,
        // Size}. `id` (the catalogue code, e.g. "CB023") is what the UI's `Bay No` column shows,
        // stored here as `referenceDataBayId`; `bayNo` (the catalogue's human label, e.g.
        // "BAY No.023") is what the UI's `Description` column shows, stored as `bayNoSnapshot`. This
        // REPLACES an earlier Description that read `bayTypeLabel` ("cargo bay rental") - that
        // mapping is gone, not merely superseded.
        referenceDataBayId: option.id,
        bayNoSnapshot: option.bayNo ?? undefined,
        sizeLabel: bayOptionSizeLabel(option),
      },
    ]);
    closeDrawer();
  };

  const removeBay = (bayId: string) => {
    onChange(value.filter((bay) => bay.referenceDataBayId !== bayId));
    setMenuAnchor(null);
  };

  return (
    <div className="py-2" data-testid="bay-hire-form-bays-section">
      <h4 className="text-sm text-gray-700 mb-2">{t("bay-hire.form.bays")}</h4>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-form-bay-options-error">
          {t("bay-hire.form.bay-options-error")}
        </Alert>
      )}

      <table className="w-full text-sm" data-testid="bay-hire-form-bays-table">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="px-3 py-2 text-sm font-semibold">{t("bay-hire.form.bayNoColumn")}</th>
            <th className="px-3 py-2 text-sm font-semibold">{t("bay-hire.form.bayDescription")}</th>
            <th className="px-3 py-2 text-sm font-semibold">{t("bay-hire.form.baySize")}</th>
            <th className="px-3 py-2 text-sm font-semibold text-right">
              {t("bay-hire.form.bayAction")}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* An empty selection still renders one placeholder row, matching the wireframe's "- -".
              A table that vanishes when empty gives the user nothing to aim at. */}
          {value.length === 0 && (
            <tr className="border-b border-gray-100" data-testid="bay-hire-form-bays-empty">
              <td className="px-3 py-2">-</td>
              <td className="px-3 py-2">-</td>
              <td className="px-3 py-2">-</td>
              <td className="px-3 py-2" />
            </tr>
          )}
          {value.map((bay) => (
            <tr key={bay.referenceDataBayId} className="border-b border-gray-100">
              <td className="px-3 py-2">{bay.referenceDataBayId}</td>
              <td className="px-3 py-2">{bay.bayNoSnapshot ?? "-"}</td>
              <td className="px-3 py-2">{bay.sizeLabel ?? "-"}</td>
              <td className="px-3 py-2 text-right">
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label={t("bay-hire.form.bayAction")}
                  data-testid={`bay-hire-form-bay-action-${bay.referenceDataBayId}`}
                  onClick={(event) =>
                    setMenuAnchor({ el: event.currentTarget, bayId: bay.referenceDataBayId })
                  }
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor?.el ?? null}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          data-testid="bay-hire-form-bay-remove"
          onClick={() => menuAnchor && removeBay(menuAnchor.bayId)}
        >
          <DeleteOutlineIcon fontSize="small" className="mr-2" />
          {t("common.remove", { defaultValue: "Remove" })}
        </MenuItem>
      </Menu>

      {error && (
        <p className="text-xs text-red-600 mt-1" data-testid="bay-hire-form-bays-error">
          {error}
        </p>
      )}

      <div className="flex justify-end mt-2">
        <Button
          variant="outlined"
          size="small"
          // Disabled until BOTH a Port and a Request Bay are chosen - the offered list is scoped
          // by the former (the catalogue query itself) and filtered by the latter (client-side).
          // Enabling it first would show an empty drawer with no explanation.
          disabled={disabled || !port || !bayPreference}
          data-testid="bay-hire-form-add-bay"
          onClick={() => setDrawerOpen(true)}
        >
          <span className="normal-case font-medium">{t("bay-hire.form.add-bay")}</span>
        </Button>
      </div>

      {drawerOpen && (
        <SideDrawer
          isOpen={drawerOpen}
          title={t("bay-hire.form.add-bay")}
          onClose={closeDrawer}
          onSubmit={confirmAddBay}
          submitLabel={t("bay-hire.form.add-bay")}
          cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
          isSubmitDisabled={!pendingBayId}
          // Was unset (defaults to "fit-content"), which sized the drawer to its single dropdown -
          // narrow enough that the footer's "Cancel"/"Add bay" buttons wrapped. SIDE_DRAWER_MAX_WIDTH
          // is the same 400px every other SimpleSideDrawer-based confirm-style drawer in the app
          // uses (BulkApproveSecurityClearanceDrawer, etc.), not a value invented for this one.
          width={SIDE_DRAWER_MAX_WIDTH}
        >
          {/* `03_Bay Hire License-3.png`: ONE `Bay No` dropdown, committed by the drawer's own
              `Add bay` footer button.

              🔴 This replaced a table of rows each with its own inline Add button, which added the bay
              and closed the drawer immediately - leaving the footer's Submit button wired to nothing
              at all. SimpleSideDrawer always renders that button, so "no onSubmit" does not mean "no
              button", it means a dead one. The wireframe's shape avoids that by construction: the
              footer button is the commit action. */}
          <div className="px-6 py-4" data-testid="bay-hire-form-add-bay-drawer">
            {selectableOptions.length === 0 ? (
              <p className="text-sm text-gray-600" data-testid="bay-hire-form-no-bays-available">
                {t("bay-hire.form.no-bays-available")}
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-1">{t("bay-hire.form.bayNoColumn")}</p>
                {/* 🔴 Was a raw MUI `<TextField select>`. That component portals its menu to
                    <body> at the MUI default theme.zIndex.modal (1300) - BELOW this drawer's own
                    z-index (1401, SIDE_DRAWER_Z_INDEX). The menu rendered but was painted BEHIND
                    the drawer paper: visible as an empty expansion with nothing clickable, exactly
                    the "unable to select" symptom. `layout.style.ts` already documents this class
                    of bug and defines SIDE_DRAWER_POPPER_Z_INDEX for it - but nothing outside a
                    test actually used it.
                    The house `Select` (`@components/Select/Select`) sidesteps the whole problem
                    with `disablePortal: true`: its menu renders inline in the DOM instead of a
                    portal, so it is never subject to a portal z-index race in the first place.
                    Every FormItem `type="select"` field already goes through it - Request Bay and
                    Payment Type on this same form work today only because of that. */}
                <Select
                  testId="bay-hire-form-bay-no-select"
                  value={pendingBayId}
                  onChange={setPendingBayId}
                  // Megaplan (2026-08-05): previously kept MUI's default (content-hugging) width,
                  // which looked narrower than the drawer footer's Cancel/Add bay buttons spanning
                  // the drawer's full content width - fullWidth matches the combo to that span.
                  fullWidth
                  placeholder={t("bay-hire.form.bayNoColumn")}
                  // Each option shows BOTH the Bay No (the catalogue id, e.g. "CB023" - matching
                  // what the Bays table shows under that header once added) and the Description
                  // (the catalogue's own bayNo label, e.g. "BAY No.023" - matching the table's
                  // Description column) so the choice is legible before adding, not just after.
                  options={selectableOptions.map((option) => ({
                    value: option.id ?? "",
                    label: bayOptionSelectLabel(option),
                  }))}
                />
              </>
            )}
          </div>
        </SideDrawer>
      )}
    </div>
  );
};

export default BaySelectionSection;
